import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { DocumentGenerationParams } from "../../../../api/graphql";
import { DocumentGenerationParamsListStore } from "../../store";

import { diaDeCalendario, formatearDia } from "../../../../common/calendarDate";

export type GridExpressProps = {
  listStore?: DocumentGenerationParamsListStore;
};

export const DocumentGenerationParamsListForm: React.FC<GridExpressProps> =
  observer(({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<DocumentGenerationParams>[] = useMemo(
      () => [],
      []
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<DocumentGenerationParams>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "contractEffectiveDate") {
          return {
            ...col,
            id: "contractEffectiveDate",
            accessorFn: (row: DocumentGenerationParams) =>
              formatearDia(
                diaDeCalendario(row.contractEffectiveDate),
                langStore.timeFormat
              ) || "-",
          };
        }
        if (col.accessorKey === "contractEndDate") {
          return {
            ...col,
            id: "contractEndDate",
            accessorFn: (row: DocumentGenerationParams) =>
              formatearDia(
                diaDeCalendario(row.contractEndDate),
                langStore.timeFormat
              ) || "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.documentGenerationParamsCurrentPage || [];

    return (
      <div id={"ListOfDocumentGenerationParams"}>
        <div id={"StartOfListingOfDocumentGenerationParams"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row) => store.clickOnRow(row)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  });
